import { useState, useMemo, useCallback } from 'react';
import { SearchableItem, SearchConfig, createFuzzySearch, performSearch } from '../utils/fuzzySearch.ts';
import { useDebounce } from './useDebounce.ts';

interface UseFuzzySearchReturn<T> {
  results: Array<{
    item: T;
    score?: number;
    matches?: any; // Using any for now to avoid Fuse namespace issues
  }>;
  query: string;
  debouncedQuery: string;
  setQuery: (query: string) => void;
  clearSearch: () => void;
}

/**
 * React hook for fuzzy search functionality with debouncing
 * @param items - Array of items to search through  
 * @param config - Search configuration options
 * @param debounceDelay - Delay in milliseconds for debouncing (default: 1000ms)
 * @returns Object with search results and control functions
 */
export function useFuzzySearch<T extends SearchableItem>(
  items: T[],
  config: SearchConfig<T>,
  debounceDelay: number = 1000
): UseFuzzySearchReturn<T> {
  const [query, setQuery] = useState('');
  
  // Debounce the search query - waits 1 full second after user stops typing
  const debouncedQuery = useDebounce(query, debounceDelay);

  // Create Fuse instance - memoized to avoid recreation on every render
  const fuse = useMemo(() => {
    return createFuzzySearch(items, config);
  }, [items, config]);

  // Perform search - memoized based on debouncedQuery and fuse instance
  // Only executes 1 second after user stops typing
  const results = useMemo(() => {
    if (!debouncedQuery.trim()) {
      return [];
    }
    return performSearch(fuse, debouncedQuery);
  }, [fuse, debouncedQuery]);

  // Clear search function
  const clearSearch = useCallback(() => {
    setQuery('');
  }, []);

  return {
    results,
    query,
    debouncedQuery,
    setQuery,
    clearSearch,
  };
}

export default useFuzzySearch;
